import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Menu, Search, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { navLinks, siteConfig } from "../../../data/siteConfig";
import { useFavorites } from "../../../contexts/FavoritesContext";
import ThemeToggle from "../../../contexts/ThemeToggle";
import { cn } from "../../../utils/cn";

const HeaderComponent: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count } = useFavorites();
  const location = useLocation();
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (headerRef.current && !headerRef.current.contains(target)) {
        setMobileOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [mobileOpen]);

  return (
    <header
      ref={headerRef}
      className={cn(
        "sticky top-0 z-40 border-b transition-all duration-300",
        scrolled
          ? "border-slate-200 bg-white/95 shadow-sm backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95"
          : "border-transparent bg-white dark:bg-slate-950"
      )}
    >
      <div className="flex items-center justify-between w-full h-20 gap-4 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <Link
          to="/"
          aria-label={`Accueil — ${siteConfig.name}`}
          className="flex shrink-0 items-center gap-2.5 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950"
        >
          <img
            src="/images/logo/fulllogo.png"
            alt={siteConfig.name}
            className="h-14 w-auto max-w-[180px] object-contain sm:h-16 sm:max-w-[210px]"
          />
        </Link>

        <nav className="items-center hidden gap-1 lg:flex" aria-label="Navigation principale">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                cn(
                  "rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                  isActive
                    ? "bg-primary-50 text-primary-700 dark:bg-primary-950/50 dark:text-primary-300"
                    : "text-slate-700 hover:bg-slate-100 hover:text-primary-700 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-primary-300"
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/recherche"
            aria-label="Rechercher sur le site"
            className="flex items-center justify-center w-10 h-10 transition-colors bg-white border rounded-xl border-slate-300 text-slate-700 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-primary-600 dark:hover:bg-primary-950/50 dark:hover:text-primary-300"
          >
            <Search size={18} />
          </Link>

          <Link
            to="/favoris"
            aria-label={`Mes favoris${count > 0 ? `, ${count} élément${count > 1 ? "s" : ""}` : ""}`}
            className="relative flex items-center justify-center w-10 h-10 transition-colors bg-white border rounded-xl border-slate-300 text-slate-700 hover:border-rose-400 hover:bg-rose-50 hover:text-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-rose-700 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
          >
            <Heart size={18} />
            {count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full border-2 border-white bg-rose-600 px-1 text-[10px] font-extrabold text-white dark:border-slate-950">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </Link>

          <ThemeToggle className="hidden sm:flex" />

          <Link
            to="/profils"
            className="hidden rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm shadow-primary-600/25 transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-400 dark:focus-visible:ring-offset-slate-950 lg:inline-flex"
          >
            Trouver un pro
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((current) => !current)}
            aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            className="flex items-center justify-center w-10 h-10 transition-colors bg-white border rounded-xl border-slate-300 text-slate-800 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:hover:border-primary-600 dark:hover:bg-primary-950/50 dark:hover:text-primary-300 lg:hidden"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {mobileOpen && (
          <motion.div
            id="mobile-navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden bg-white border-t shadow-lg border-slate-200 text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-white lg:hidden"
          >
            <nav className="flex flex-col gap-1 px-4 py-4" aria-label="Navigation mobile">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "rounded-xl px-3 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                      isActive
                        ? "bg-primary-50 text-primary-700 dark:bg-primary-950/60 dark:text-primary-300"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-white"
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}

              <Link
                to="/recherche"
                className="flex items-center gap-2 px-3 py-3 text-sm font-semibold transition-colors rounded-xl text-slate-700 hover:bg-slate-100 hover:text-primary-700 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-primary-300"
              >
                <Search size={17} />
                Rechercher
              </Link>

              <Link
                to="/favoris"
                className="flex items-center gap-2 px-3 py-3 text-sm font-semibold transition-colors rounded-xl text-slate-700 hover:bg-rose-50 hover:text-rose-600 dark:text-slate-200 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
              >
                <Heart size={17} />
                Mes favoris {count > 0 && `(${count})`}
              </Link>

              <div className="flex items-center justify-between px-3 py-2 mt-2 border rounded-xl border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="text-sm font-semibold">Thème</span>
                <ThemeToggle />
              </div>

              <Link
                to="/profils"
                className="px-4 py-3 mt-2 text-sm font-bold text-center text-white transition-colors rounded-xl bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400"
              >
                Trouver un professionnel
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default HeaderComponent;
